/**
 * Centralized API Client with Enterprise-Grade Error Handling
 * 
 * Features:
 * - Automatic retry with exponential backoff
 * - Request timeouts
 * - User-friendly error messages
 * - Network error handling
 * - Token refresh on 401
 * - Consistent error format
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;

interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    errorId?: string;
    details?: Record<string, unknown>;
  };
}

interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  skipAuth?: boolean;
}

/**
 * User-friendly error messages for common error codes
 */
const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: 'Your session has expired. Please sign in again.',
  FORBIDDEN: "You don't have permission to perform this action.",
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  SERVICE_UNAVAILABLE: 'The service is temporarily unavailable. Please try again in a moment.',
  INTERNAL_ERROR: 'Something went wrong. Our team has been notified.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  TIMEOUT_ERROR: 'The request took too long. Please try again.',
  CONFLICT: 'This action conflicts with existing data. Please refresh and try again.',
};

/**
 * Check if an error is retryable
 */
function isRetryableError(status: number, _errorCode?: string): boolean {
  // Retry on network errors (status 0) or server errors (5xx)
  if (status === 0 || (status >= 500 && status < 600)) {
    return true;
  }
  
  // Retry on rate limiting (429)
  if (status === 429) {
    return true;
  }
  
  // Retry on service unavailable (503)
  if (status === 503) {
    return true;
  }
  
  // Don't retry client errors (4xx) except 429 and 503
  return false;
}

/**
 * Get user-friendly error message
 */
function getUserFriendlyMessage(errorCode: string, defaultMessage: string): string {
  return ERROR_MESSAGES[errorCode] ?? defaultMessage ?? 'An unexpected error occurred. Please try again.';
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fetch with timeout
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('TIMEOUT_ERROR');
    }
    throw error;
  }
}

/**
 * Get access token from auth store
 */
function getAccessToken(): string | null {
  try {
    const authData = localStorage.getItem('profitos-auth');
    if (!authData) return null;
    const parsed = JSON.parse(authData);
    return parsed.state?.accessToken ?? null;
  } catch {
    return null;
  }
}

/**
 * Refresh access token
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = (await response.json()) as ApiResponse<{ accessToken: string }>;
    if (data.success && data.data.accessToken) {
      // Update stored token
      const authData = localStorage.getItem('profitos-auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        parsed.state.accessToken = data.data.accessToken;
        localStorage.setItem('profitos-auth', JSON.stringify(parsed));
      }
      return data.data.accessToken;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get tenant ID from auth store
 */
function getTenantId(): string | null {
  try {
    const authData = localStorage.getItem('profitos-auth');
    if (!authData) return null;
    const parsed = JSON.parse(authData);
    return parsed.state?.tenant?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Make an API request with retry logic and error handling
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    timeout = DEFAULT_TIMEOUT,
    retries = MAX_RETRIES,
    skipAuth = false,
    ...fetchOptions
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  // Add authentication header if not skipped
  const headers = new Headers(fetchOptions.headers);
  if (!skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    // Add tenant ID header if available
    const tenantId = getTenantId();
    if (tenantId) {
      headers.set('X-Tenant-ID', tenantId);
    }
  }
  
  // Set content type if body is provided and not already set
  if (fetchOptions.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let lastError: Error | null = null;
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Exponential backoff for retries
      if (attempt > 0) {
        const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
        await sleep(delayMs);
      }

      // Make request with timeout
      const response = await fetchWithTimeout(url, {
        ...fetchOptions,
        headers,
        credentials: 'include',
      }, timeout);

      lastResponse = response;

      // Handle 401 Unauthorized - try to refresh token once
      if (response.status === 401 && !skipAuth && attempt === 0) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          headers.set('Authorization', `Bearer ${newToken}`);
          continue; // Retry with new token
        }
        // If refresh failed, continue to error handling
      }

      // Parse response
      let data: ApiResponse<T>;
      try {
        const text = await response.text();
        if (!text) {
          // Empty response
          if (response.ok) {
            return undefined as unknown as T;
          }
          throw new Error('Empty response from server');
        }
        data = JSON.parse(text) as ApiResponse<T>;
      } catch (parseError) {
        // Non-JSON response
        if (response.ok) {
          throw new Error('Invalid response format from server');
        }
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      // Handle error response
      if (!response.ok || !data.success) {
        const errorCode = data.success === false ? data.error.code : 'INTERNAL_ERROR';
        const errorMessage = data.success === false 
          ? getUserFriendlyMessage(errorCode, data.error.message)
          : 'An unexpected error occurred';
        
        // Check if retryable
        if (isRetryableError(response.status, errorCode) && attempt < retries) {
          lastError = new Error(errorMessage);
          continue; // Retry
        }
        
        // Non-retryable error or max retries reached
        const apiError = new Error(errorMessage);
        (apiError as Error & { code?: string; status?: number; errorId?: string }).code = errorCode;
        (apiError as Error & { code?: string; status?: number; errorId?: string }).status = response.status;
        if (data.success === false && data.error.errorId) {
          (apiError as Error & { code?: string; status?: number; errorId?: string }).errorId = data.error.errorId;
        }
        throw apiError;
      }

      // Success response
      return data.data;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Handle timeout
      if (lastError.message === 'TIMEOUT_ERROR') {
        if (attempt < retries) {
          continue; // Retry on timeout
        }
        throw new Error(getUserFriendlyMessage('TIMEOUT_ERROR', 'The request took too long. Please try again.'));
      }
      
      // Handle network errors
      if (lastError.message.includes('Failed to fetch') || 
          lastError.message.includes('NetworkError') ||
          lastError.name === 'TypeError') {
        if (attempt < retries) {
          continue; // Retry on network error
        }
        throw new Error(getUserFriendlyMessage('NETWORK_ERROR', 'Unable to connect to the server. Please check your internet connection.'));
      }
      
      // If we have a response, check if retryable
      if (lastResponse && isRetryableError(lastResponse.status) && attempt < retries) {
        continue; // Retry
      }
      
      // Re-throw if not retryable or max retries reached
      throw lastError;
    }
  }

  // Should never reach here, but TypeScript needs it
  throw lastError ?? new Error('Request failed after retries');
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions): Promise<T> => {
    return apiRequest<T>(endpoint, { ...options, method: 'GET' });
  },
  
  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> => {
    return apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  },
  
  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> => {
    return apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  },
  
  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> => {
    return apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  },
  
  delete: <T>(endpoint: string, options?: RequestOptions): Promise<T> => {
    return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
  },
};
